package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.DashboardDetail
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlin.math.abs
import kotlin.time.Clock

sealed class MonthlyBreakdownScreenState {
    data object Loading : MonthlyBreakdownScreenState()
    data class Error(val message: String) : MonthlyBreakdownScreenState()
    data class Loaded(
        val yearMonth: String,
        val totalIncome: Double,
        val totalExpenses: Double,
        val netIncome: Double,
        val savingsRate: Double,
        val incomeTransactions: List<Transaction>,
        val expenseTransactions: List<Transaction>,
        val accountNames: Map<String, String> = emptyMap(),
        val institutionNames: Map<String, String?> = emptyMap(),
        val transactionDashboardIds: Map<String, String?> = emptyMap()
    ) : MonthlyBreakdownScreenState()
}

fun parseYearMonth(raw: String): Pair<Int, Int>? {
    val monthNames = listOf(
        "jan", "feb", "mar", "apr", "may", "jun",
        "jul", "aug", "sep", "oct", "nov", "dec"
    )
    val fullMonthNames = listOf(
        "january", "february", "march", "april", "may", "june",
        "july", "august", "september", "october", "november", "december"
    )
    val normalized = raw.replace("%20", " ").replace("+", " ").trim()
    val tokens = normalized.split(" ", "-", "_", "/").filter { it.isNotEmpty() }
    if (tokens.isEmpty()) return null

    var foundYear: Int? = null
    var foundMonth: Int? = null

    for (token in tokens) {
        val lower = token.lowercase()
        val shortIdx = monthNames.indexOf(lower)
        if (shortIdx >= 0) {
            foundMonth = shortIdx + 1
            continue
        }
        val fullIdx = fullMonthNames.indexOf(lower)
        if (fullIdx >= 0) {
            foundMonth = fullIdx + 1
            continue
        }
        val num = token.toIntOrNull()
        if (num != null) {
            if (num in 1900..2100) {
                foundYear = num
            } else if (num in 1..12 && foundMonth == null) {
                foundMonth = num
            }
        }
    }

    if (foundMonth != null && foundYear == null) {
        foundYear = Clock.System.todayIn(TimeZone.currentSystemDefault()).year
    }

    return if (foundYear != null && foundMonth != null) {
        foundYear to foundMonth
    } else null
}

@OptIn(ExperimentalCoroutinesApi::class)
class MonthlyBreakdownViewModel(
    private val yearMonth: String,
    private val accountUseCase: AccountUseCase,
    private val transactionRepository: TransactionRepository,
    private val dashboardUseCase: DashboardUseCase
) : ViewModel() {

    private val _state = MutableStateFlow<MonthlyBreakdownScreenState>(MonthlyBreakdownScreenState.Loading)
    val state: StateFlow<MonthlyBreakdownScreenState> = _state.asStateFlow()

    init {
        startFlowCollection()
    }

    fun refresh() {
        viewModelScope.launch {
            transactionRepository.refresh()
        }
    }

    private fun startFlowCollection() {
        viewModelScope.launch {
            val dashboardsDetailsFlow = dashboardUseCase.GetDashboards().flatMapLatest { dashboardsResponse ->
                when (dashboardsResponse) {
                    is Response.Error -> flowOf(Response.Error(dashboardsResponse.message))
                    Response.Loading -> flowOf(Response.Loading)
                    is Response.Success -> {
                        val dashboards = dashboardsResponse.data
                        if (dashboards.isEmpty()) {
                            flowOf(Response.Success(emptyList<DashboardDetail>()))
                        } else {
                            val detailFlows = dashboards.map { dashboard ->
                                dashboardUseCase.GetDashboardDetail(dashboard.dashboardId)
                            }
                            combine(detailFlows) { detailsResponses ->
                                val error = detailsResponses.filterIsInstance<Response.Error>().firstOrNull()
                                if (error != null) {
                                    Response.Error(error.message)
                                } else if (detailsResponses.any { it is Response.Loading }) {
                                    Response.Loading
                                } else {
                                    val details = detailsResponses.filterIsInstance<Response.Success<DashboardDetail>>().map { it.data }
                                    Response.Success(details)
                                }
                            }
                        }
                    }
                }
            }

            combine(
                dashboardsDetailsFlow,
                accountUseCase.GetAccounts(),
                accountUseCase.GetPlaidItems(),
                transactionRepository.transactions
            ) { detailsResponse, accountsResponse, plaidItemsResponse, transactionsResponse ->
                when {
                    detailsResponse is Response.Error ->
                        MonthlyBreakdownScreenState.Error(detailsResponse.message)
                    accountsResponse is Response.Error ->
                        MonthlyBreakdownScreenState.Error(accountsResponse.message)
                    plaidItemsResponse is Response.Error ->
                        MonthlyBreakdownScreenState.Error(plaidItemsResponse.message)
                    transactionsResponse is Response.Error ->
                        MonthlyBreakdownScreenState.Error(transactionsResponse.message)
                    detailsResponse is Response.Success && accountsResponse is Response.Success && plaidItemsResponse is Response.Success && transactionsResponse is Response.Success -> {
                        val dashboards = detailsResponse.data
                        val dashboardTransactions = dashboards
                            .flatMap { it.transactions }
                            .distinctBy { it.transactionId }

                        val nonInvestmentAccountIds = dashboards
                            .flatMap { it.accounts }
                            .filter { it.type.lowercase() != "investment" }
                            .map { it.accountId }
                            .toSet()

                        val relevantTransactions = dashboardTransactions.filter { tx ->
                            if (nonInvestmentAccountIds.isNotEmpty()) {
                                tx.accountId in nonInvestmentAccountIds
                            } else true
                        }

                        val parsedDate = parseYearMonth(yearMonth)
                        val targetYear = parsedDate?.first
                        val targetMonth = parsedDate?.second

                        val txsForMonth = if (targetYear != null && targetMonth != null) {
                            relevantTransactions.filter { tx ->
                                try {
                                    val parsed = LocalDate.parse(tx.date)
                                    parsed.year == targetYear && parsed.month.number == targetMonth
                                } catch (e: Exception) {
                                    false
                                }
                            }
                        } else {
                            emptyList()
                        }

                        val incomeTransactions = mutableListOf<Transaction>()
                        val expenseTransactions = mutableListOf<Transaction>()

                        var totalIncome = 0.0
                        var totalExpenses = 0.0

                        txsForMonth.forEach { tx ->
                            val primary = tx.activeCategory?.primary ?: tx.category.firstOrNull() ?: ""
                            val detailed = tx.activeCategory?.detailed ?: ""
                            if (primary == "TRANSFER_IN" || primary == "TRANSFER_OUT" ||
                                detailed == "LOAN_PAYMENTS_CREDIT_CARD_PAYMENT" ||
                                tx.category.any { cat -> cat.equals("Credit Card", ignoreCase = true) || cat.equals("Credit Card Payment", ignoreCase = true) }
                            ) {
                                return@forEach
                            }
                            val amount = tx.amount
                            if (amount < 0.0 || primary == "INCOME") {
                                totalIncome += abs(amount)
                                incomeTransactions.add(tx)
                            } else if (amount > 0.0 && primary != "INCOME") {
                                totalExpenses += amount
                                expenseTransactions.add(tx)
                            }
                        }

                        val sortedIncome = incomeTransactions.sortedByDescending { it.date }
                        val sortedExpenses = expenseTransactions.sortedByDescending { it.date }

                        val netIncome = totalIncome - totalExpenses
                        val savingsRate = if (totalIncome > 0.0) ((netIncome / totalIncome) * 100.0).coerceIn(-1000.0, 100.0) else 0.0

                        val accountNames = mutableMapOf<String, String>()
                        val institutionNames = mutableMapOf<String, String?>()
                        val plaidItems = plaidItemsResponse.data

                        accountsResponse.data.forEach { acc ->
                            accountNames[acc.accountId] = acc.name
                            val institutionName = plaidItems.find { it.itemId == acc.itemId }?.institutionName
                            institutionNames[acc.accountId] = institutionName
                        }

                        dashboards.flatMap { it.accounts }.forEach { acc ->
                            accountNames[acc.accountId] = acc.name
                            institutionNames[acc.accountId] = acc.institutionName
                        }

                        val transactionDashboardIds = mutableMapOf<String, String?>()
                        dashboards.forEach { dashboardDetail ->
                            dashboardDetail.transactions.forEach { tx ->
                                transactionDashboardIds[tx.transactionId] = dashboardDetail.dashboardId
                            }
                        }

                        MonthlyBreakdownScreenState.Loaded(
                            yearMonth = yearMonth,
                            totalIncome = totalIncome,
                            totalExpenses = totalExpenses,
                            netIncome = netIncome,
                            savingsRate = savingsRate,
                            incomeTransactions = sortedIncome,
                            expenseTransactions = sortedExpenses,
                            accountNames = accountNames,
                            institutionNames = institutionNames,
                            transactionDashboardIds = transactionDashboardIds
                        )
                    }
                    else -> MonthlyBreakdownScreenState.Loading
                }
            }.collect { newState ->
                _state.value = newState
            }
        }
    }
}
